// Firebase Analytics utility module
// Centralized analytics tracking for the Bloom IQ web application

import { analytics, logEvent } from '../firebase';
import { setUserId, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { sanitizePageLocation } from './pageLocation';

// ============================================================================
// EVENT CONSTANTS
// ============================================================================

export const EVENTS = {
  // Page views
  PAGE_VIEW: 'page_view',

  // Authentication events
  LOGIN: 'login',
  LOGIN_ATTEMPT: 'login_attempt',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_ERROR: 'login_error',
  SIGN_UP: 'sign_up',
  SIGN_UP_ATTEMPT: 'sign_up_attempt',
  SIGN_UP_SUCCESS: 'sign_up_success',
  SIGN_UP_ERROR: 'sign_up_error',
  LOGOUT: 'logout',
  PASSWORD_RESET_REQUEST: 'password_reset_request',
  PASSWORD_RESET_SUCCESS: 'password_reset_success',

  // Home page events
  QUICK_REPORTS_CLICK: 'quick_reports_click',
  ALERT_DIGEST_TAB_CHANGED: 'alert_digest_tab_changed',
  PREFERRED_COMMODITIES_UPDATED: 'preferred_commodities_updated',

  // Analytics page events
  COMMODITY_SELECTED: 'commodity_selected',
  DATE_FILTER_CHANGED: 'date_filter_changed',
  WIDGET_ADDED: 'widget_added',
  WIDGET_REMOVED: 'widget_removed',
  FONT_SIZE_CHANGED: 'font_size_changed',

  // BiQ chat events
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_STREAMING_STOPPED: 'chat_streaming_stopped',
  HINT_CARD_CLICKED: 'hint_card_clicked',
  REPORT_GENERATED: 'report_generated',
  DEEP_THINKING_TOGGLED: 'deep_thinking_toggled',

  // Market Trends events
  MARKET_TRENDS_CLICK: 'market_trends_click',
  MARKET_TRENDS_GENERATED: 'market_trends_generated',
  MARKET_TRENDS_ERROR: 'market_trends_error',

  // Alerts events
  ALERT_TYPE_CHANGED: 'alert_type_changed',
  ALERT_STEP_COMPLETED: 'alert_step_completed',
  COMMODITY_TOGGLE: 'commodity_toggle',
  REGION_SELECTED: 'region_selected',
  SMART_ALERT_CREATED: 'smart_alert_created',
  CUSTOM_ALERT_CREATED: 'custom_alert_created',
  ALERT_CREATION_ERROR: 'alert_creation_error',

  // Account events
  PROFILE_UPDATED: 'profile_updated',
  PROFILE_UPDATE_ERROR: 'profile_update_error',
  WHATSAPP_LINKED: 'whatsapp_linked',
  WHATSAPP_REMOVED: 'whatsapp_removed',

  // BiQ popup events
  BIQ_POPUP_OPENED: 'biq_popup_opened',
  BIQ_POPUP_CLOSED: 'biq_popup_closed',

  // Settings events
  LANGUAGE_CHANGED: 'language_changed',
};

// Page name mapping for readable analytics
const PAGE_NAMES = {
  '/': 'BiQ Chat',
  '/overview': 'Overview',
  '/login': 'Login',
  '/signup': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/auth/callback': 'Auth Callback',
  '/verify-email': 'Email Verification',
  '/analytics': 'Analytics',
  '/terminal': 'Analytics',
  '/lookerstudio': 'Looker Studio',
  '/biq': 'BiQ Chat',
  '/chatbot': 'BiQ Chat',
  '/produce-planner': 'Produce Planner',
  '/map': 'Global Produce Map',
  '/sea-tracking': 'Global Produce Map',
  '/alerts': 'Alerts',
  '/alerts/configure': 'Configure Alerts',
  '/alerts/enabled': 'Enabled Alerts',
  '/alerts/triggered': 'Triggered Alerts',
  '/alerts/edit': 'Edit Alert',
  '/account': 'Account',
  '/billing': 'Billing',
  '/membership': 'Manage Membership',
  '/faq': 'FAQ',
  '/contact': 'Contact Us',
  '/privacy': 'Privacy Policy',
  '/usage': 'Usage',
  '/subscription': 'Subscription',
  '/signup-success': 'Signup Success',
};

const getPageTitle = (pagePath) => {
  const normalizedPath = pagePath.replace(/\/$/, '') || '/';
  const localizedMatch = normalizedPath.match(/^\/(en|es)(\/.*)?$/);
  const titlePath = localizedMatch ? (localizedMatch[2] || '/') : normalizedPath;
  if (PAGE_NAMES[titlePath]) return PAGE_NAMES[titlePath];
  if (titlePath.startsWith('/alerts/edit/')) return PAGE_NAMES['/alerts/edit'];
  return document.title;
};

// ============================================================================
// TRACKING FUNCTIONS
// ============================================================================

/**
 * Track a page view event
 * @param {string} pagePath - The path of the page (e.g., '/login')
 * @param {string} [pageTitle] - Optional page title override
 */
export const trackPageView = (pagePath, pageTitle) => {
  const title = pageTitle || getPageTitle(pagePath);
  document.title = title;

  logEvent(analytics, EVENTS.PAGE_VIEW, {
    page_path: pagePath,
    page_title: title,
    page_location: sanitizePageLocation(window.location.href),
  });
};

/** GA4 page title for the producing-regions chart (Looker "Page title and screen name"). */
export const GLOBAL_PRODUCE_MAP_TITLE = 'Global Produce Map';

/**
 * Virtual page_view when the Global Produce Map chart loads on Analytics (or any
 * route that embeds ProducingRegionsMap). Does not change document.title or the
 * parent route's Analytics page_view.
 *
 * @param {string} [commodity] - Optional commodity for GA4 custom dimension
 */
export const trackGlobalProduceMapView = (commodity) => {
  if (!analytics) return;
  const basePath = window.location.pathname.replace(/\/$/, '') || '/';
  const virtualPath = `${basePath}/global-produce-map`;
  logEvent(analytics, EVENTS.PAGE_VIEW, {
    page_title: GLOBAL_PRODUCE_MAP_TITLE,
    page_location: `${window.location.origin}${virtualPath}`,
    page_path: virtualPath,
    ...(commodity ? { commodity } : {}),
  });
};

/** Once per browser session — first successful map load only (recommended for Looker Views). */
let mapViewSentThisSession = false;

export const trackGlobalProduceMapViewOncePerSession = (commodity) => {
  if (mapViewSentThisSession) return;
  mapViewSentThisSession = true;
  trackGlobalProduceMapView(commodity);
};

/**
 * Track a custom event with optional parameters
 * @param {string} eventName - The name of the event
 * @param {Object} [params] - Optional event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  logEvent(analytics, eventName, params);
};

/**
 * Set the user ID for analytics tracking
 * @param {string} userId - The user's unique identifier (email or UID)
 */
export const setAnalyticsUserId = (userId) => {
  if (userId) {
    setUserId(analytics, userId);
  }
};

/**
 * Set user properties for segmentation
 * @param {Object} properties - User properties to set
 */
export const setUserAnalyticsProperties = (properties) => {
  if (properties && Object.keys(properties).length > 0) {
    firebaseSetUserProperties(analytics, properties);
  }
};

/**
 * Extract email domain for B2B analytics
 * @param {string} email - User's email address
 * @returns {string} - The email domain
 */
export const getEmailDomain = (email) => {
  if (!email || !email.includes('@')) return 'unknown';
  return email.split('@')[1];
};

// ============================================================================
// AUTHENTICATION TRACKING HELPERS
// ============================================================================

/**
 * Track login attempt
 * @param {string} method - Login method ('email', 'google', 'apple')
 */
export const trackLoginAttempt = (method) => {
  trackEvent(EVENTS.LOGIN_ATTEMPT, { method });
};

/**
 * Track successful login
 * @param {string} method - Login method ('email', 'google', 'apple')
 * @param {string} [email] - User's email for domain tracking
 */
export const trackLoginSuccess = (method, email) => {
  const params = { method };
  if (email) {
    params.email_domain = getEmailDomain(email);
  }
  trackEvent(EVENTS.LOGIN_SUCCESS, params);
  trackEvent(EVENTS.LOGIN, params); // Standard Firebase login event
};

/**
 * Track login error
 * @param {string} method - Login method ('email', 'google', 'apple')
 * @param {string} errorCode - Error code from Firebase
 */
export const trackLoginError = (method, errorCode) => {
  trackEvent(EVENTS.LOGIN_ERROR, { method, error_code: errorCode });
};

/**
 * Track signup attempt
 * @param {string} method - Signup method ('email', 'google', 'apple')
 */
export const trackSignUpAttempt = (method) => {
  trackEvent(EVENTS.SIGN_UP_ATTEMPT, { method });
};

/**
 * Track successful signup
 * @param {string} method - Signup method ('email', 'google', 'apple')
 * @param {string} [email] - User's email for domain tracking
 */
export const trackSignUpSuccess = (method, email) => {
  const params = { method };
  if (email) {
    params.email_domain = getEmailDomain(email);
  }
  trackEvent(EVENTS.SIGN_UP_SUCCESS, params);
  trackEvent(EVENTS.SIGN_UP, params); // Standard Firebase sign_up event
};

/**
 * Track signup error
 * @param {string} method - Signup method ('email', 'google', 'apple')
 * @param {string} errorCode - Error code from Firebase
 */
export const trackSignUpError = (method, errorCode) => {
  trackEvent(EVENTS.SIGN_UP_ERROR, { method, error_code: errorCode });
};

/**
 * Track user logout
 */
export const trackLogout = () => {
  trackEvent(EVENTS.LOGOUT);
  // Clear user ID after logout event is logged
  setUserId(analytics, null);
};

// ============================================================================
// FEATURE TRACKING HELPERS
// ============================================================================

/**
 * Track commodity selection
 * @param {string} commodity - The selected commodity
 * @param {string} page - The page where selection occurred
 */
export const trackCommoditySelected = (commodity, page = 'analytics') => {
  trackEvent(EVENTS.COMMODITY_SELECTED, { commodity, page });
};

/**
 * Track chat message sent
 * @param {number} messageLength - Length of the message
 */
export const trackChatMessageSent = (messageLength) => {
  trackEvent(EVENTS.CHAT_MESSAGE_SENT, { message_length: messageLength });
};

/**
 * Track report generation
 * @param {string} commodity - The commodity for the report
 */
export const trackReportGenerated = (commodity) => {
  trackEvent(EVENTS.REPORT_GENERATED, { commodity });
};

/**
 * Track alert creation
 * @param {string} alertType - 'smart' or 'custom'
 * @param {string} commodity - The commodity for the alert
 * @param {string} specificType - The specific alert type
 */
export const trackAlertCreated = (alertType, commodity, specificType) => {
  const eventName = alertType === 'smart' ? EVENTS.SMART_ALERT_CREATED : EVENTS.CUSTOM_ALERT_CREATED;
  trackEvent(eventName, { 
    alert_type: alertType,
    commodity,
    specific_type: specificType,
  });
};

// ============================================================================
// REACT HOOKS
// ============================================================================

/**
 * Hook to automatically track page views on route changes
 * Should be used at the App level inside BrowserRouter
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
  }, [location.pathname]);
};

/**
 * Hook to set user properties when user data changes
 * @param {Object} userDetails - User details from context
 * @param {Object} firebaseUser - Firebase user object
 */
export const useUserAnalytics = (userDetails, firebaseUser) => {
  useEffect(() => {
    if (firebaseUser?.uid) {
      // Set user ID using Firebase UID (not email) to avoid PII in analytics
      setAnalyticsUserId(firebaseUser.uid);

      // Set user properties (email_domain is hashed/aggregated, not PII)
      const properties = {
        email_domain: firebaseUser.email ? getEmailDomain(firebaseUser.email) : 'unknown',
        is_verified: firebaseUser.emailVerified ? 'true' : 'false',
      };

      if (userDetails?.tier) {
        properties.user_tier = userDetails.tier;
      }

      setUserAnalyticsProperties(properties);
    }
  }, [firebaseUser?.uid, firebaseUser?.email, firebaseUser?.emailVerified, userDetails?.tier]);
};

export default {
  EVENTS,
  GLOBAL_PRODUCE_MAP_TITLE,
  trackPageView,
  trackGlobalProduceMapView,
  trackGlobalProduceMapViewOncePerSession,
  trackEvent,
  setAnalyticsUserId,
  setUserAnalyticsProperties,
  trackLoginAttempt,
  trackLoginSuccess,
  trackLoginError,
  trackSignUpAttempt,
  trackSignUpSuccess,
  trackSignUpError,
  trackLogout,
  trackCommoditySelected,
  trackChatMessageSent,
  trackReportGenerated,
  trackAlertCreated,
  usePageTracking,
  useUserAnalytics,
};

